<template>
  <div class="pr-6">
    <div v-if="loading" class="space-y-3">
      <div class="grid grid-cols-3 gap-3">
        <div v-for="index in 3" :key="index" class="h-16 animate-pulse rounded-xl bg-gray-100"></div>
      </div>
      <div v-for="index in 3" :key="`row-${index}`" class="h-24 animate-pulse rounded-xl bg-gray-50"></div>
    </div>

    <template v-else>
      <div class="mb-5 grid grid-cols-3 gap-3">
        <div class="rounded-xl bg-orange-50 px-3 py-3">
          <p class="text-xs font-medium text-orange-800">Acheteurs</p>
          <p class="mt-1 text-2xl font-semibold leading-none text-black">{{ acheteurs }}</p>
        </div>
        <div class="rounded-xl bg-gray-50 px-3 py-3">
          <p class="text-xs font-medium text-gray-500">Minimum de notre boutique</p>
          <p class="mt-1 text-2xl font-semibold leading-none text-black">
            {{ commandeMini > 0 ? formatCurrency(commandeMini) : '—' }}
          </p>
        </div>
        <div class="rounded-xl bg-gray-50 px-3 py-3">
          <p class="text-xs font-medium text-gray-500">Montant des paniers</p>
          <p class="mt-1 text-2xl font-semibold leading-none text-black">{{ formatCurrency(montant) }}</p>
        </div>
      </div>

      <div v-if="paniers.length > 1" class="mb-4 flex items-center gap-2">
        <span class="text-xs font-medium text-gray-500">Trier par</span>
        <button
          type="button"
          class="rounded-full px-3 py-1 text-xs font-medium"
          :class="sortBy === 'date' ? 'bg-black text-white' : 'bg-gray-100 text-black'"
          @click="sortBy = 'date'"
        >
          Date
        </button>
        <button
          type="button"
          class="rounded-full px-3 py-1 text-xs font-medium"
          :class="sortBy === 'prix' ? 'bg-black text-white' : 'bg-gray-100 text-black'"
          @click="sortBy = 'prix'"
        >
          Prix
        </button>
      </div>

      <p v-if="hiddenPaniers > 0" class="mb-3 text-xs text-gray-500">
        {{ paniers.length }} panier{{ paniers.length > 1 ? 's' : '' }} affiché{{ paniers.length > 1 ? 's' : '' }}
        sur {{ acheteurs }}.
      </p>

      <div v-if="paniers.length === 0" class="rounded-xl bg-gray-50 px-4 py-10 text-center">
        <i class="fas fa-shopping-basket text-2xl text-gray-300"></i>
        <p class="mt-3 text-sm text-gray-500">Aucun panier en cours pour le moment.</p>
      </div>

      <div v-else class="space-y-3">
        <article
          v-for="panier in pagePaniers"
          :key="panier.id"
          class="overflow-hidden rounded-xl border border-gray-100"
        >
          <button
            type="button"
            class="flex w-full items-center gap-3 bg-gray-50 px-3 py-2.5 text-left"
            @click="togglePanier(panier.id)"
          >
            <i
              class="fas fa-chevron-right shrink-0 text-xs text-gray-400 transition-transform"
              :class="isOpen(panier.id) ? 'rotate-90' : ''"
            ></i>
            <span class="min-w-0 flex-1 truncate text-sm font-semibold text-black">
              {{ panierLabel(panier) }}
            </span>
            <span class="shrink-0 text-xs text-gray-500">
              {{ panier.produits.length }} produit{{ panier.produits.length > 1 ? 's' : '' }}
            </span>
            <span class="shrink-0 text-sm font-semibold text-black">{{ formatCurrency(panier.montant) }}</span>
          </button>

          <ul v-show="isOpen(panier.id)">
            <li
              v-for="produit in panier.produits"
              :key="produit.id"
              class="flex items-center gap-3 border-t border-gray-100 px-3 py-2"
            >
              <button
                v-if="produit.imageUrl && !failedImages[produit.id]"
                type="button"
                class="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-gray-100 cursor-zoom-in"
                title="Voir les photos"
                @click.stop="openPhotos(produit)"
              >
                <img
                  :src="produit.imageUrl"
                  :alt="produit.reference"
                  class="h-full w-full object-cover"
                  @error="failedImages[produit.id] = true"
                />
              </button>
              <div v-else class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-semibold text-gray-400">
                {{ getInitial(produit.reference) }}
              </div>
              <div class="min-w-0 flex-1">
                <p
                  class="truncate text-sm"
                  :class="produit.rupture ? 'font-medium text-red-600' : 'text-black'"
                >
                  {{ produit.reference }}
                  <span v-if="produit.rupture"> · Rupture</span>
                </p>
                <p
                  v-if="produit.categorie"
                  class="truncate text-xs"
                  :class="produit.rupture ? 'text-red-500' : 'text-gray-500'"
                >
                  {{ produit.categorie }}
                </p>
              </div>
              <p
                class="shrink-0 text-xs font-medium"
                :class="produit.rupture ? 'text-red-600' : 'text-gray-600'"
              >
                {{ produit.quantite }} pcs
              </p>
            </li>
          </ul>
        </article>
      </div>

      <UnifiedPagination
        v-if="paniers.length > perPage"
        class="!mt-4 !mb-0"
        :current-page="page"
        :total-items="paniers.length"
        :items-per-page="perPage"
        item-label="paniers"
        :show-items-per-page="false"
        :show-first-last-buttons="true"
        @update:current-page="page = $event"
      />
    </template>

    <Teleport to="body">
      <div
        v-if="galleryOpen"
        class="fixed inset-0 z-[10050] flex items-center justify-center bg-black/80 p-4"
        @click.self="closePhotos"
      >
        <button
          type="button"
          class="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          aria-label="Fermer"
          @click="closePhotos"
        >
          <i class="fas fa-times"></i>
        </button>

        <button
          v-if="galleryPhotos.length > 1"
          type="button"
          class="absolute left-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          aria-label="Photo précédente"
          @click.stop="showPreviousPhoto"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <button
          v-if="galleryPhotos.length > 1"
          type="button"
          class="absolute right-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          aria-label="Photo suivante"
          @click.stop="showNextPhoto"
        >
          <i class="fas fa-chevron-right"></i>
        </button>

        <div class="flex max-h-[90vh] max-w-[95vw] flex-col items-center gap-3">
          <img
            v-if="currentGalleryPhoto"
            :src="currentGalleryPhoto"
            :alt="galleryTitle"
            class="max-h-[80vh] max-w-[95vw] rounded-2xl object-contain shadow-2xl"
          />
          <p class="text-sm text-white">
            {{ galleryTitle }}
            <span v-if="galleryPhotos.length > 1"> · {{ galleryIndex + 1 }} / {{ galleryPhotos.length }}</span>
            <span v-if="galleryLoading"> · chargement…</span>
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, reactive, ref, watch } from 'vue'
import UnifiedPagination from '@/components/ui/UnifiedPagination.vue'
import { getProductPhotos } from '@/api/shooting'
import type { PanierAcheteurRow, ProduitDansPanier } from '@/types'

const props = withDefaults(defineProps<{
  paniers: PanierAcheteurRow[]
  loading?: boolean
  acheteurs?: number
  montant?: number
  commandeMini?: number
}>(), {
  paniers: () => [],
  loading: false,
  acheteurs: 0,
  montant: 0,
  commandeMini: 0,
})

const perPage = 10
const page = ref(1)
const openIds = ref<number[]>([])
const sortBy = ref<'date' | 'prix'>('date')

const sortedPaniers = computed(() => {
  const rows = [...props.paniers]
  if (sortBy.value === 'prix') {
    return rows.sort((a, b) => b.montant - a.montant)
  }
  return rows.sort((a, b) => cartTime(b.date) - cartTime(a.date))
})

const pagePaniers = computed(() => {
  const start = (page.value - 1) * perPage
  return sortedPaniers.value.slice(start, start + perPage)
})

const hiddenPaniers = computed(() =>
  Math.max(0, props.acheteurs - props.paniers.length),
)

watch(() => props.paniers, () => {
  page.value = 1
  openIds.value = []
})

watch(page, () => {
  openIds.value = []
})

watch(sortBy, () => {
  page.value = 1
  openIds.value = []
})

const cartTime = (value?: string | null) => {
  if (!value) return 0
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : 0
}

const formatDate = (value?: string | null) => {
  const time = cartTime(value)
  if (!time) return ''
  const date = new Date(time)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}/${month}/${date.getFullYear()}`
}

const panierLabel = (panier: PanierAcheteurRow) => {
  const date = formatDate(panier.date)
  const title = date ? `Panier du ${date}` : 'Panier'
  return panier.pays ? `${title} · ${panier.pays}` : title
}

const isOpen = (id: number) => openIds.value.includes(id)

const togglePanier = (id: number) => {
  openIds.value = isOpen(id)
    ? openIds.value.filter((openId) => openId !== id)
    : [...openIds.value, id]
}

const failedImages = reactive<Record<number, boolean>>({})
const galleryOpen = ref(false)
const galleryLoading = ref(false)
const galleryPhotos = ref<string[]>([])
const galleryIndex = ref(0)
const galleryTitle = ref('')

const currentGalleryPhoto = computed(() => galleryPhotos.value[galleryIndex.value] ?? '')

const closePhotos = () => {
  galleryOpen.value = false
  galleryPhotos.value = []
  galleryIndex.value = 0
}

const showPreviousPhoto = () => {
  if (galleryPhotos.value.length < 2) return
  galleryIndex.value = (galleryIndex.value - 1 + galleryPhotos.value.length) % galleryPhotos.value.length
}

const showNextPhoto = () => {
  if (galleryPhotos.value.length < 2) return
  galleryIndex.value = (galleryIndex.value + 1) % galleryPhotos.value.length
}

const onGalleryKey = (event: KeyboardEvent) => {
  if (!galleryOpen.value) return
  if (event.key === 'Escape') closePhotos()
  if (event.key === 'ArrowLeft') showPreviousPhoto()
  if (event.key === 'ArrowRight') showNextPhoto()
}

window.addEventListener('keydown', onGalleryKey)
onUnmounted(() => window.removeEventListener('keydown', onGalleryKey))

const openPhotos = async (produit: ProduitDansPanier) => {
  if (!produit.imageUrl) return
  galleryTitle.value = produit.reference
  galleryPhotos.value = [produit.imageUrl]
  galleryIndex.value = 0
  galleryOpen.value = true
  galleryLoading.value = true
  try {
    const data = await getProductPhotos(produit.id, 'fiche')
    const photos = (data.photos ?? []).filter((url) => Boolean(url))
    if (photos.length > 0) galleryPhotos.value = photos
  } catch {
    galleryPhotos.value = [produit.imageUrl]
  } finally {
    galleryLoading.value = false
  }
}

const formatCurrency = (value: number) =>
  `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

const getInitial = (reference: string) =>
  (reference || '?').trim().charAt(0).toUpperCase() || '?'
</script>
